import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchWithAuth } from '../../../utils/api/fetchWithAuth';
import { useAuthStore } from '../../../utils/stores/useAuthStore';
import { useSessionStore } from '../../../utils/stores/useSessionStore';

import { useEffect } from 'react';
import { BASE_URL } from '../../../utils/Constants';
import { useChatNotificationsStore } from '../../../utils/stores/useChatNotificationsStore';
import { ChatSession } from '../types/ChatSession';

async function getChatSessions(userId: string, idToken: string) {
  const response = await fetchWithAuth(`${BASE_URL}/api/chat/user/${userId}/sessions`, {
    method: 'GET',
    headers: {
      Authorization: idToken,
    },
  });

  if (response.status === 401) {
    useSessionStore.getState().setSessionExpired(true);
    throw new Error('Unauthorized');
  }

  if (!response.ok) {
    throw new Error('Failed to fetch chat sessions');
  }
  const data = await response.json();

  return data as ChatSession[];
}

const useGetChatSessions = () => {
  const { auth } = useAuthStore();
  const userId = auth.userId;
  const idToken = auth.idToken;
  const queryClient = useQueryClient();
  const setUnreadSessions = useChatNotificationsStore(
    (state) => state.setUnreadSessions,
  );

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['chatSessions', userId, idToken],
    queryFn: () => getChatSessions(userId!, idToken!),
    enabled: !!userId,
    refetchInterval: 60 * 1000,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  useEffect(() => {
    if (data) {
      // Check for unread messages
      const unreadSessions = data.filter(
        (session: ChatSession) =>
          new Date(session.last_message_sent_at) >
          new Date(session.last_read_user),
      );
      unreadSessions.forEach((session: ChatSession) => {
        queryClient.invalidateQueries({ queryKey: ['messages', session.id] });
      });
      setUnreadSessions(unreadSessions);
    }
  }, [data, setUnreadSessions, queryClient, userId]);

  return { data, isLoading, error, refetch, isFetching };
};

export default useGetChatSessions;
